// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageBackNavigation from '../components/PageBackNavigation';
import { useLocation, useNavigate } from 'react-router-dom';
import InlineAtHomeSupportPanel from '../components/InlineAtHomeSupportPanel';
import { fetchFamilyDescriptions } from '../utils/familyDescriptions';
import { canonicalSubject, standardProgressScore, statusFromColor, statusFromScore, resolveStrandLabel } from '../utils/progressStatus';
import { fetchAllVocabBanks, HighlightVocabText } from '../utils/vocabBank';
import {
  fetchAtHomeSupport,
  fetchStudentProgressionGrid,
  fetchSupportBankGrades,
  fetchSupportBankResourceBlob,
  fetchSupportBankResourceFiles,
  fetchSupportBankSubjects,
} from '../services/api';

const isLikelyUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());
const normalizeLookupName = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// Maps normalized CSV resource labels to the actual file name returned by the server
// when the file stem differs from the label used in the CSV.
const RESOURCE_FILE_LABEL_ALIASES = {
  consonantdigraphwordbank:    'Digraph Word Bank',
  longvowelwordbank:           'Long Vowels Word Bank',
  rcontrolledvowelwordbank:    'R-Controlled Word Bank',
  latinandgreekrootswordbank:  'Greek and Latin Roots Word Bank',
  gameboards:                  'Board Games',
  gameboard:                   'Board Games',
};

const EXTERNAL_RESOURCE_LINKS = {
  phonicsprogression: 'https://canva.link/miif0r4ayb4jrxr',
  abccards: 'https://canva.link/g5bcm9h4zrglbgf',
  abc: 'https://canva.link/g5bcm9h4zrglbgf',
};

function getYouTubeEmbedUrl(value) {
  const url = String(value || '').trim();
  if (!url) return '';

  const match = url.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (!match) return '';

  return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
}

function tokenizeForMatch(value) {
  const stopWords = new Set(['the', 'a', 'an', 'in', 'on', 'of', 'to', 'by', 'and', 'or', 'for', 'with', 'is', 'are', 'be', 'from', 'that', 'this']);
  return String(value || '')
    .toLowerCase()
    .split(/\W+/)
    .filter((word) => word.length > 3 && !stopWords.has(word));
}

function scoreEntryForContext(entry, { standard, skill, domain }) {
  const standardText = String(entry?.standard || '').toLowerCase();
  const strandText = String(entry?.strand || '').toLowerCase();
  const friendlyText = String(entry?.friendly || '').toLowerCase();
  const haystack = `${standardText} ${strandText} ${friendlyText}`;
  let score = 0;

  const wantedStandard = String(standard || '').trim().toLowerCase();
  const wantedSkill = String(skill || '').trim().toLowerCase();
  const wantedDomain = String(domain || '').trim().toLowerCase();

  if (wantedStandard) {
    if (standardText === wantedStandard) score += 100;
    else if (standardText.includes(wantedStandard)) score += 40;
  }

  if (wantedSkill) {
    if (strandText === wantedSkill) score += 60;
    else if (strandText.includes(wantedSkill) || friendlyText.includes(wantedSkill)) score += 30;
    for (const token of tokenizeForMatch(wantedSkill)) {
      if (haystack.includes(token)) score += 5;
    }
  }

  if (wantedDomain) {
    if (friendlyText.includes(wantedDomain) || strandText.includes(wantedDomain)) score += 20;
    for (const token of tokenizeForMatch(wantedDomain)) {
      if (haystack.includes(token)) score += 4;
    }
  }

  return score;
}

function progressStatusFromColor(color) {
  const c = String(color || '').toLowerCase();
  if (c === 'red') return 'not_yet';
  if (c === 'yellow') return 'working';
  if (c === 'green') return 'mastered';
  if (c === 'lightgreen') return 'on_track';
  return 'na';
}

function progressStatusUi(status) {
  if (status === 'not_yet') return { label: 'Not Yet', bg: 'var(--ae-status-not-yet)', fg: '#fff', border: 'var(--ae-status-not-yet)' };
  if (status === 'working') return { label: 'Learning Zone', bg: '#fef3c7', fg: '#92400e', border: '#fcd34d' };
  if (status === 'on_track') return { label: 'On Track', bg: '#d5f5e2', fg: '#166534', border: '#7DDDA5' };
  if (status === 'mastered') return { label: 'Proficient', bg: 'var(--ae-status-mastered)', fg: '#fff', border: 'var(--ae-status-mastered)' };
  return { label: 'Not Assessed', bg: '#e5e7eb', fg: '#374151', border: '#d1d5db' };
}

function normalizeStandardCode(value) {
  return String(value || '').split(':')[0].trim().replace(/\.+$/, '').toLowerCase();
}

function formatLearningOutcome(entry, subject, vocabBank, active) {
  const friendly = String(entry?.friendly || '').trim();
  if (friendly) {
    return (
      <div style={{ fontSize: 14, lineHeight: 1.6, color: '#1f2937' }}>
        {active ? (
          <HighlightVocabText text={friendly} subject={subject} vocabBank={vocabBank} active={active} />
        ) : friendly}
      </div>
    );
  }
  const standard = String(entry?.standard || '').trim();
  if (standard) {
    return <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a8a' }}>{standard}</div>;
  }
  return <div style={{ fontSize: 14, lineHeight: 1.6, color: '#1f2937' }}>Coming soon</div>;
}

const PROGRESSION_CHARTS = {
  phonics: '/phonics.png',
  fluency: '/fluency.png',
};

const SUBJECT_GRADE_BADGES = {
  phonics: {
    '1': { title: 'Phonics Guide', lexile: '170L - 510L', wpm: '20-40' },
    '2': { title: 'Phonics Guide', lexile: '460L - 700L', wpm: '50-90' },
    '3': { title: 'Phonics Guide', lexile: '640L - 850L', wpm: '72-110' },
    '4': { title: 'Phonics Guide', lexile: '790L - 1000L', wpm: '90-125' },
    '5': { title: 'Phonics Guide', lexile: '840L - 1130L', wpm: '110-140' },
  },
  fluency: {
    '1': { title: 'Fluency Guide', lexile: '170L - 510L', wpm: '20-40' },
    '2': { title: 'Fluency Guide', lexile: '460L - 700L', wpm: '50-90' },
    '3': { title: 'Fluency Guide', lexile: '640L - 850L', wpm: '72-110' },
    '4': { title: 'Fluency Guide', lexile: '790L - 1000L', wpm: '90-125' },
    '5': { title: 'Fluency Guide', lexile: '840L - 1130L', wpm: '110-140' },
  },
};

const PHONICS_BADGE_LEVELS = Array.from({ length: 10 }, (_, idx) => idx + 1);

function getGuideBadgeForSubject(subject, grade) {
  const subjectKey = String(subject || '').trim().toLowerCase();
  const normalizedGrade = String(grade || '').trim();
  return SUBJECT_GRADE_BADGES[subjectKey]?.[normalizedGrade] || null;
}

function getPhonicsBadgeLevel(grade, entry) {
  const numericGrade = Number(String(grade || '').trim());
  if (!Number.isNaN(numericGrade) && numericGrade >= 1 && numericGrade <= 10) {
    return numericGrade;
  }

  const searchText = String(entry?.standard || entry?.strand || entry?.friendly || '').toLowerCase();
  const match = searchText.match(/phonics\s*(\d{1,2})|\b(10|[1-9])\b/);
  if (match) {
    const level = Number(match[1] || match[2]);
    if (!Number.isNaN(level) && level >= 1 && level <= 10) {
      return level;
    }
  }

  return null;
}

export default function AtHomeSupportPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = location.state?.returnTo;
  const isFamilyPortal = Boolean(location.pathname?.startsWith('/parent'));
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialGrade = query.get('grade') || '';
  const initialSubject = query.get('subject') || '';
  const initialSkill = query.get('skill') || '';
  const initialStandard = query.get('standard') || '';
  const initialDomain = query.get('domain') || '';
  const hasInitialStandard = Boolean(initialStandard);
  const selectedStudentId = query.get('studentId') || query.get('student_id') || '';
  const selectedClassId = query.get('classId') || query.get('class_id') || '';
  const isDomainAction = isFamilyPortal && Boolean(selectedStudentId && initialDomain);

  const [grades, setGrades] = useState<any>([]);
  const [subjects, setSubjects] = useState<any>([]);
  const [selectedGrade, setSelectedGrade] = useState(initialGrade);
  const [selectedSubject, setSelectedSubject] = useState(initialSubject);
  const [selectedSkill, setSelectedSkill] = useState(initialSkill);
  const [selectedStandard, setSelectedStandard] = useState(initialStandard);
  const [selectedDomain, setSelectedDomain] = useState(initialDomain);
  const [search, setSearch] = useState<any>('');
  const [entries, setEntries] = useState<any>([]);
  const [resourceFiles, setResourceFiles] = useState<any>([]);
  const [studentProgressRows, setStudentProgressRows] = useState<any>([]);
  const [progressLoading, setProgressLoading] = useState<any>(false);
  const [progressError, setProgressError] = useState<any>('');
  const [loading, setLoading] = useState<any>(false);
  const [error, setError] = useState<any>('');
  const [resourceError, setResourceError] = useState<any>('');
  const [familyDescriptions, setFamilyDescriptions] = useState<any>(null);
  const [familyLoading, setFamilyLoading] = useState<any>(false);
  const [familyError, setFamilyError] = useState<any>('');
  const [vocabBanks, setVocabBanks] = useState<any>({});

  useEffect(() => {
    if (!isFamilyPortal) return undefined;
    let cancelled = false;
    fetchAllVocabBanks()
      .then((banks) => {
        if (!cancelled) setVocabBanks(banks);
      })
      .catch((err) => {
        if (!cancelled) console.error('Could not load vocab banks in AtHomeSupportPage:', err);
      });
    return () => { cancelled = true; };
  }, [isFamilyPortal]);

  // Sync URL search params when navigation state or query string updates
  useEffect(() => {
    if (initialGrade) setSelectedGrade(initialGrade);
    if (initialSubject) setSelectedSubject(initialSubject);
    if (initialSkill) setSelectedSkill(initialSkill);
    if (initialStandard) setSelectedStandard(initialStandard);
    if (initialDomain) setSelectedDomain(initialDomain);
  }, [initialGrade, initialSubject, initialSkill, initialStandard, initialDomain]);

  // Load family descriptions on mount
  useEffect(() => {
    let cancelled = false;
    setFamilyLoading(true);
    fetchFamilyDescriptions()
      .then((dict) => {
        if (!cancelled) setFamilyDescriptions(dict || {});
      })
      .catch((error) => {
        console.warn('Could not load family-friendly resources:', error);
        if (!cancelled) {
          setFamilyDescriptions({});
          setFamilyError('Family-friendly resources could not be loaded. Standard descriptions are shown where available.');
        }
      })
      .finally(() => {
        if (!cancelled) setFamilyLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Load available grades on mount
  useEffect(() => {
    fetchSupportBankGrades()
      .then((d) => setGrades(d.grades || []))
      .catch(() => setError('Could not load grades.'));

    fetchSupportBankResourceFiles()
      .then((d) => setResourceFiles(d.files || []))
      .catch(() => setResourceError('Some downloadable resources may be unavailable right now.'));
  }, []);

  // Load subjects when grade changes
  useEffect(() => {
    fetchSupportBankSubjects(selectedGrade)
      .then((d) => {
        const nextSubjects = d.subjects || [];
        setSubjects(nextSubjects);

        const findSubjectMatch = (value) => {
          const wanted = String(value || '').trim().toLowerCase();
          if (!wanted) return '';
          return nextSubjects.find((s) => String(s || '').trim().toLowerCase() === wanted) || '';
        };

        const selectedMatch = findSubjectMatch(selectedSubject);
        if (selectedMatch) {
          if (selectedMatch !== selectedSubject) setSelectedSubject(selectedMatch);
          return;
        }

        const initialMatch = findSubjectMatch(initialSubject);
        if (initialMatch) {
          setSelectedSubject(initialMatch);
          return;
        }

        if (selectedSubject && (isFamilyPortal || familyDescriptions?.[normalizeStandardCode(selectedStandard)])) {
          return;
        }

        if (!selectedSubject) {
          return;
        }
        setSelectedSubject('');
      })
      .catch(() => {});
  }, [selectedGrade, selectedSubject, initialSubject, isFamilyPortal, familyDescriptions, selectedStandard]);

  // Load entries — filter only by grade/subject on the server.
  // Strand/skill/standard matching happens client-side via selectedEntry.
  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const supportData = await fetchAtHomeSupport({
        grade: isDomainAction ? undefined : selectedGrade || undefined,
        subject: selectedSubject || undefined,
      });
      setEntries(supportData.entries || []);
    } catch (_err: any) {
      setError('Could not load support content.');
    } finally {
      setLoading(false);
    }
  }, [selectedGrade, selectedSubject, isDomainAction]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // If the page is opened from a specific student tile, load that student's progression
  // so parents can see progress status next to support resources.
  useEffect(() => {
    if (!selectedStudentId) {
      setStudentProgressRows([]);
      setProgressError('');
      return;
    }

    let cancelled = false;
    setProgressLoading(true);
    setProgressError('');

    fetchStudentProgressionGrid(selectedStudentId, selectedClassId || undefined)
      .then((rows) => {
        if (cancelled) return;
        setStudentProgressRows(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (cancelled) return;
        setProgressError('Could not load student progress status.');
      })
      .finally(() => {
        if (cancelled) return;
        setProgressLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedClassId, selectedStudentId]);

  const domainActionResources = useMemo(() => {
    if (!isDomainAction) return [];
    const seen = new Set();
    return studentProgressRows.flatMap(row => {
      const standard = row?.standard || {};
      const point = row?.data_point;
      if (!point) return [];
      const score = standardProgressScore(point);
      const status = score !== null ? statusFromScore(score) : statusFromColor(point.color);
      if (status !== 'working') return [];
      if (canonicalSubject(standard.subject) !== canonicalSubject(initialSubject)) return [];
      const domain = String(standard.domain_description || standard.domain || 'General').trim() || 'General';
      if (normalizeLookupName(domain) !== normalizeLookupName(initialDomain)) return [];
      const code = normalizeStandardCode(standard.mls_code);
      if (!code || seen.has(code)) return [];
      seen.add(code);
      const family = Object.entries(familyDescriptions || {}).find(
        ([key]) => normalizeStandardCode(key) === code,
      )?.[1];
      const support = entries.find(entry => normalizeStandardCode(entry.standard) === code);
      return [{
        code: standard.mls_code,
        subject: canonicalSubject(standard.subject),
        grade: standard.grade_level || '',
        domain,
        strand: resolveStrandLabel(standard, ''),
        hasFamilySupport: Boolean(family),
        supportEntry: {
          ...support,
          standard: standard.mls_code,
          friendly: support?.friendly || standard.standard_description || '',
        },
        familySupport: {
          ...family,
          standard: family?.standard || support?.friendly || standard.standard_description || '',
          video: family?.video || support?.video_url || support?.video || '',
          activity: family?.activity || support?.activity || support?.digital || support?.print || '',
        },
      }];
    });
  }, [isDomainAction, studentProgressRows, initialSubject, initialDomain, familyDescriptions, entries]);

  const [domainResourceIndex, setDomainResourceIndex] = useState<any>(0);
  useEffect(() => {
    setDomainResourceIndex(0);
  }, [selectedStudentId, initialSubject, initialDomain]);
  const activeDomainIndex = Math.min(domainResourceIndex, Math.max(0, domainActionResources.length - 1));
  const domainResource = domainActionResources[activeDomainIndex];

  const filteredEntries = useMemo(() => {
    const q = search.toLowerCase();
    return entries.filter((e) => {
      if (!q) return true;
      return (
        e.standard.toLowerCase().includes(q) ||
        e.friendly.toLowerCase().includes(q) ||
        e.strand.toLowerCase().includes(q)
      );
    });
  }, [entries, search]);

  const progressByStandard = useMemo(() => {
    const map = new Map();
    for (const row of studentProgressRows) {
      const standardCode = String(row?.standard?.mls_code || '').trim().toLowerCase();
      if (!standardCode || map.has(standardCode)) continue;
      const status = progressStatusFromColor(row?.data_point?.color);
      map.set(standardCode, status);
    }
    return map;
  }, [studentProgressRows]);

  // Requested view mode: show all progress states except n/a.
  const visibleEntries = useMemo(() => {
    if (!selectedStudentId) return filteredEntries;
    const withoutNa = filteredEntries.filter((entry) => {
      const status = progressByStandard.get(String(entry.standard || '').trim().toLowerCase()) || 'na';
      return status !== 'na';
    });
    // If standards between progress grid and support bank don't line up exactly,
    // avoid a blank page and fall back to the full filtered support list.
    return withoutNa.length ? withoutNa : filteredEntries;
  }, [filteredEntries, progressByStandard, selectedStudentId]);

  useEffect(() => {
    if (!visibleEntries.length || isFamilyPortal) return;

    const normalize = (value) => String(value || '').trim().toLowerCase();
    const skillLow = normalize(selectedSkill);
    const standardLow = normalize(selectedStandard);
    const domainLow = normalize(selectedDomain);

    const skillMatches = skillLow
      ? visibleEntries.some((entry) => {
          const strand = normalize(entry.strand);
          const friendly = normalize(entry.friendly);
          return (
            strand === skillLow ||
            strand.includes(skillLow) ||
            friendly.includes(skillLow) ||
            skillLow.includes(strand)
          );
        })
      : true;

    const standardMatches = standardLow
      ? visibleEntries.some((entry) => normalize(entry.standard).includes(standardLow))
      : true;

    const domainMatches = domainLow
      ? visibleEntries.some((entry) => {
          const strand = normalize(entry.strand);
          const friendly = normalize(entry.friendly);
          return strand.includes(domainLow) || friendly.includes(domainLow);
        })
      : true;

    if (!skillMatches && selectedSkill) setSelectedSkill('');
    if (!standardMatches && selectedStandard && !hasInitialStandard) setSelectedStandard('');
    if (!domainMatches && selectedDomain) setSelectedDomain('');
  }, [hasInitialStandard, isFamilyPortal, selectedDomain, selectedSkill, selectedStandard, visibleEntries]);

  const displayGrades = useMemo(() => {
    const set = new Set(grades);
    if (familyDescriptions) {
      Object.values(familyDescriptions).forEach((entry) => {
        if (entry?.grade) set.add(String(entry.grade));
      });
    }
    const gradeOrder = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9-10', '11-12', '9-12'];
    return Array.from(set).sort((a, b) => {
      const ai = gradeOrder.indexOf(String(a));
      const bi = gradeOrder.indexOf(String(b));
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    });
  }, [grades, familyDescriptions]);

  const displaySubjects = useMemo(() => {
    const set = new Set(subjects);
    if (familyDescriptions) {
      Object.values(familyDescriptions).forEach((entry) => {
        if (entry?.subject) set.add(canonicalSubject(entry.subject));
      });
    }
    return Array.from(set).filter(Boolean);
  }, [subjects, familyDescriptions]);

  const familyStandardsList = useMemo(() => {
    if (!familyDescriptions) return [];
    const items = Object.entries(familyDescriptions).map(([code, val]) => ({
      code,
      standard: code,
      subject: canonicalSubject(val.subject),
      grade: String(val.grade || '').trim(),
      domain: val.domainName || val.domain || '',
      strand: val.strandName || val.strand || '',
      friendly: val.standard || val.standardDescription || '',
      ...val,
    }));

    return items.filter((item) => {
      if (selectedGrade && String(item.grade).toLowerCase() !== String(selectedGrade).toLowerCase()) {
        return false;
      }
      if (selectedSubject && canonicalSubject(item.subject) !== canonicalSubject(selectedSubject)) {
        return false;
      }
      if (search) {
        const q = search.toLowerCase();
        return (
          item.code.toLowerCase().includes(q) ||
          item.standard.toLowerCase().includes(q) ||
          item.friendly.toLowerCase().includes(q) ||
          item.strand.toLowerCase().includes(q) ||
          item.domain.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [familyDescriptions, selectedGrade, selectedSubject, search]);

  const familyStrandsGrouped = useMemo(() => {
    const map = {};
    for (const item of familyStandardsList) {
      const s = item.strandName || item.strand;
      if (s) {
        map[s] = (map[s] || 0) + 1;
      }
    }
    return map;
  }, [familyStandardsList]);

  const selectedFamilyEntry = useMemo(() => {
    if (!familyDescriptions) return null;
    if (selectedStandard) {
      const target = normalizeStandardCode(selectedStandard);
      const direct = Object.entries(familyDescriptions).find(([k]) => normalizeStandardCode(k) === target);
      if (direct) return { code: direct[0], ...direct[1] };
    }

    if (isFamilyPortal && familyStandardsList.length > 0) {
      if (selectedSkill) {
        const skillLow = String(selectedSkill).toLowerCase();
        const match = familyStandardsList.find((e) => {
          const s = String(e.strand || '').toLowerCase();
          const sName = String(e.strandName || '').toLowerCase();
          return s === skillLow || sName === skillLow || s.includes(skillLow) || sName.includes(skillLow) || skillLow.includes(s) || skillLow.includes(sName);
        });
        if (match) return match;
      }
      return familyStandardsList[0];
    }

    return null;
  }, [familyDescriptions, selectedStandard, isFamilyPortal, familyStandardsList, selectedSkill]);

  const activeFamilyIndex = useMemo(() => {
    if (!selectedFamilyEntry || !familyStandardsList.length) return -1;
    const targetCode = normalizeStandardCode(selectedFamilyEntry.code);
    return familyStandardsList.findIndex((item) => normalizeStandardCode(item.code) === targetCode);
  }, [selectedFamilyEntry, familyStandardsList]);

  const isFamilyMode = isFamilyPortal || Boolean(selectedFamilyEntry);

  const selectedFamilyStatus = useMemo(() => {
    const code = normalizeStandardCode(selectedFamilyEntry?.code || selectedStandard);
    if (!code) return 'na';
    return progressByStandard.get(code) || 'na';
  }, [selectedFamilyEntry, selectedStandard, progressByStandard]);

  const skillMatchesVisibleEntries = useMemo(() => {
    if (!selectedSkill) return false;
    const skillLow = String(selectedSkill).toLowerCase();
    return visibleEntries.some((entry) => {
      const strand = String(entry.strand || '').toLowerCase();
      const friendly = String(entry.friendly || '').toLowerCase();
      return (
        strand === skillLow ||
        strand.includes(skillLow) ||
        friendly.includes(skillLow) ||
        skillLow.includes(strand)
      );
    });
  }, [selectedSkill, visibleEntries]);

  const focusedEntries = useMemo(() => {
    if (selectedStandard) {
      const wantedStandard = normalizeStandardCode(selectedStandard);
      return filteredEntries.filter((entry) => normalizeStandardCode(entry.standard) === wantedStandard);
    }

    // If a specific grade is selected, show entries for that grade/subject largely unfiltered.
    if (selectedGrade) {
      return visibleEntries;
    }

    // When a skill query is present but doesn't match any visible strand/friendly text,
    // fall back to the full visible resource list instead of narrowing to a single item.
    if (selectedSkill && !selectedStandard && !selectedDomain && !skillMatchesVisibleEntries) {
      return visibleEntries;
    }

    const hasContext = selectedStandard || selectedSkill || selectedDomain;
    if (!hasContext) return visibleEntries;

    const scored = visibleEntries
      .map((entry) => ({
        entry,
        score: scoreEntryForContext(entry, {
          standard: selectedStandard,
          skill: selectedSkill,
          domain: selectedDomain,
        }),
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score);

    if (!scored.length) return visibleEntries;

    const topScore = scored[0].score;
    return scored
      .filter(({ score }) => score >= Math.max(20, topScore - 10))
      .map(({ entry }) => entry);
  }, [filteredEntries, selectedDomain, selectedGrade, selectedSkill, selectedStandard, skillMatchesVisibleEntries, visibleEntries]);

  const grouped = useMemo(() => {
    const map = {};
    for (const entry of focusedEntries) {
      const subj = entry.subject || 'Other';
      const strand = entry.strand || 'General';
      if (!map[subj]) map[subj] = {};
      if (!map[subj][strand]) map[subj][strand] = [];
      map[subj][strand].push(entry);
    }
    return map;
  }, [focusedEntries]);

  const selectedEntry = useMemo(() => {
    if (!focusedEntries.length) return null;

    if (selectedStandard) {
      const exactStandard = focusedEntries.find(
        (entry) => normalizeStandardCode(entry.standard) === normalizeStandardCode(selectedStandard),
      );
      return exactStandard || null;
    }

    if (selectedSkill) {
      const skillLow = String(selectedSkill).toLowerCase();

      // 1. Exact strand match
      const exact = focusedEntries.find((e) => String(e.strand || '').toLowerCase() === skillLow);
      if (exact) return exact;

      // 2. Strand or friendly contains full skill string
      const partial = focusedEntries.find((e) => {
        const strand = String(e.strand || '').toLowerCase();
        const friendly = String(e.friendly || '').toLowerCase();
        return strand.includes(skillLow) || friendly.includes(skillLow);
      });
      if (partial) return partial;

      // 3. Keyword overlap — split skill into significant words and score entries
      const stopWords = new Set(['the','a','an','in','on','of','to','by','and','or','for','with','is','are','be','by','from','that','this']);
      const skillWords = skillLow.split(/\W+/).filter((w) => w.length > 3 && !stopWords.has(w));
      if (skillWords.length) {
        let best = null;
        let bestScore = 0;
        for (const e of focusedEntries) {
          const haystack = `${e.strand} ${e.friendly} ${e.standard}`.toLowerCase();
          const score = skillWords.reduce((acc, w) => acc + (haystack.includes(w) ? 1 : 0), 0);
          if (score > bestScore) { bestScore = score; best = e; }
        }
        if (best && bestScore > 0) return best;
      }
    }

    return focusedEntries[0];
  }, [focusedEntries, selectedSkill, selectedStandard]);

  const selectedEntryIndex = useMemo(() => {
    if (!selectedEntry) return -1;
    return focusedEntries.findIndex((entry) => entry.standard === selectedEntry.standard);
  }, [focusedEntries, selectedEntry]);

  const selectedEntryProgress = useMemo(() => {
    const key = String(selectedEntry?.standard || '').trim().toLowerCase();
    if (!key) return 'na';
    return progressByStandard.get(key) || 'na';
  }, [progressByStandard, selectedEntry]);

  const deriveSupportPathCategory = useCallback((entry) => {
    if (!entry) return '';
    const standardRaw = String(entry.standard || '').trim();
    const standard = standardRaw.split(':')[0].trim().toUpperCase();

    if (/\.RF\.4\.A\./.test(standard)) return 'Fluency';
    if (/\.RF\.3\.A\./.test(standard)) return 'Phonics';
    if (/\.RF\.2\.A\./.test(standard)) return 'Phonics';
    if (/\.RF\.1\.A\./.test(standard)) return 'Phonics';
    if (/\.RF\.5\.A\./.test(standard)) return 'Phonics';

    const rawCategory = String(entry.domain_description || entry.strand_description || entry.domain || entry.strand || '').trim();
    const cleaned = rawCategory.replace(/\s*-\s*\d+$/, '').trim();
    if (/^(Print|Digital)$/i.test(cleaned)) return '';
    if (/^(Phonics|Fluency)$/i.test(cleaned)) {
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
    }
    return cleaned;
  }, []);

  const selectedEntryPath = useMemo(() => {
    if (!selectedEntry) return '';
    const category = deriveSupportPathCategory(selectedEntry);
    const parts = [selectedEntry.subject, category].filter(Boolean);
    return parts.join(' › ');
  }, [selectedEntry, deriveSupportPathCategory]);

  const guideSubject = useMemo(() => {
    if (!selectedEntry) return '';
    const category = String(deriveSupportPathCategory(selectedEntry) || '').toLowerCase();
    if (category.includes('phonics')) return 'phonics';
    if (category.includes('fluency')) return 'fluency';

    const strand = String(selectedEntry?.strand || '').toLowerCase();
    if (strand.includes('phonics')) return 'phonics';
    if (strand.includes('fluency')) return 'fluency';

    const friendly = String(selectedEntry?.friendly || '').toLowerCase();
    if (friendly.includes('phonics')) return 'phonics';
    if (friendly.includes('fluency')) return 'fluency';

    return '';
  }, [selectedEntry, deriveSupportPathCategory]);

  const guideGradeLabel = useMemo(() => {
    const grade = selectedGrade || selectedEntry?.grade || '';
    return grade ? `Grade ${grade}` : '';
  }, [selectedGrade, selectedEntry]);

  const subjectGuideBadge = useMemo(() => {
    const grade = selectedGrade || selectedEntry?.grade || '';
    return getGuideBadgeForSubject(guideSubject, grade);
  }, [selectedGrade, selectedEntry, guideSubject]);

  const phonicsBadgeLevel = useMemo(() => getPhonicsBadgeLevel(selectedGrade, selectedEntry), [selectedGrade, selectedEntry]);

  const openProgressionChart = useCallback((subject) => {
    const subjectLower = String(subject || '').toLowerCase();
    const chartUrl = subjectLower === 'phonics' ? PROGRESSION_CHARTS.phonics : subjectLower === 'fluency' ? PROGRESSION_CHARTS.fluency : null;
    if (chartUrl) {
      const viewer = window.open('', '_blank', 'width=1200,height=900');
      viewer.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${subjectLower === 'phonics' ? 'Phonics Progression' : 'Fluency Guide'}</title>
            <style>
              body { margin: 0; padding: 20px; background: #f3f4f6; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              img { max-width: 100%; height: auto; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
            </style>
          </head>
          <body>
            <img src="${chartUrl}" alt="${subjectLower === 'phonics' ? 'Phonics Progression' : 'Fluency Guide'}" />
          </body>
        </html>
      `);
      viewer.document.close();
    }
  }, []);

  useEffect(() => {
    if (!focusedEntries.length) {
      if (selectedStandard && !hasInitialStandard) setSelectedStandard('');
      return;
    }

    if (!selectedStandard) {
      setSelectedStandard(focusedEntries[0].standard || '');
      return;
    }

    const exists = focusedEntries.some((entry) => normalizeStandardCode(entry.standard) === normalizeStandardCode(selectedStandard));
    if (!exists && !hasInitialStandard) {
      setSelectedStandard(focusedEntries[0].standard || '');
    }
  }, [focusedEntries, hasInitialStandard, selectedStandard]);

  const goToEntryAt = useCallback((nextIndex) => {
    if (!focusedEntries.length) return;
    const safeIndex = Math.max(0, Math.min(nextIndex, focusedEntries.length - 1));
    const nextEntry = focusedEntries[safeIndex];
    if (!nextEntry) return;
    setSelectedStandard(nextEntry.standard || '');
    setSelectedSkill(nextEntry.strand || '');
    setSelectedSubject(nextEntry.subject || '');
    setSelectedDomain('');
  }, [focusedEntries]);

  const goToPrevious = useCallback(() => {
    if (selectedEntryIndex <= 0) return;
    goToEntryAt(selectedEntryIndex - 1);
  }, [goToEntryAt, selectedEntryIndex]);

  const goToNext = useCallback(() => {
    if (selectedEntryIndex === -1 || selectedEntryIndex >= focusedEntries.length - 1) return;
    goToEntryAt(selectedEntryIndex + 1);
  }, [focusedEntries.length, goToEntryAt, selectedEntryIndex]);

  const digitalLines = useMemo(() => {
    const raw = String(selectedEntry?.digital || selectedEntry?.resource || '');
    return raw.split('\n').map((l) => l.trim()).filter(Boolean);
  }, [selectedEntry]);

  const printLines = useMemo(() => {
    const raw = String(selectedEntry?.print || '');
    return raw.split('\n').map((l) => l.trim()).filter(Boolean);
  }, [selectedEntry]);

  const videoResource = useMemo(() => {
    const rawVideo = String(selectedEntry?.video || '').trim();
    const videoName = String(selectedEntry?.video_name || '').trim();
    const explicitUrl = String(selectedEntry?.video_url || '').trim();

    const url = explicitUrl || (isLikelyUrl(rawVideo) ? rawVideo : '');
    const name = videoName || (url ? 'Open Video Resource' : rawVideo);
    const embedUrl = getYouTubeEmbedUrl(url);

    return { url, name, embedUrl };
  }, [selectedEntry]);

  const resourceFileMap = useMemo(() => {
    const map = new Map();
    for (const file of resourceFiles) {
      const keys = [file.filename, file.label]
        .map((k) => normalizeLookupName(k))
        .filter(Boolean);
      for (const key of keys) {
        if (!map.has(key)) map.set(key, file.filename);
      }
    }
    // Add aliases so CSV labels that differ from actual file stems still resolve.
    for (const [aliasNorm, actualLabel] of Object.entries(RESOURCE_FILE_LABEL_ALIASES)) {
      if (!map.has(aliasNorm)) {
        const actualNorm = normalizeLookupName(actualLabel);
        const filename = map.get(actualNorm);
        if (filename) map.set(aliasNorm, filename);
      }
    }
    return map;
  }, [resourceFiles]);

  const openSupportResource = useCallback(async (name) => {
    try {
      const { blob, filename } = await fetchSupportBankResourceBlob(name);
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1500);
    } catch (_err: any) {
      setResourceError(`Could not open resource: ${name}`);
    }
  }, []);

  const renderResourceLine = useCallback((line) => {
    const trimmed = String(line || '').trim();
    if (!trimmed) return null;

    if (isLikelyUrl(trimmed)) {
      return (
        <a href={trimmed} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11 }}>↗</span>{trimmed}
        </a>
      );
    }

    const matchingFilename = resourceFileMap.get(normalizeLookupName(trimmed));
    if (matchingFilename) {
      return (
        <button
          type="button"
          onClick={() => openSupportResource(matchingFilename)}
          style={{
            border: '1px solid #3b82f6',
            background: '#eff6ff',
            color: '#1d4ed8',
            borderRadius: 6,
            padding: '3px 10px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            textDecoration: 'none',
          }}
        >
          <span style={{ fontSize: 13 }}>⬇</span>{trimmed}
        </button>
      );
    }

    const externalLink = EXTERNAL_RESOURCE_LINKS[normalizeLookupName(trimmed)];
    if (externalLink) {
      return (
        <a href={externalLink} target="_blank" rel="noreferrer" style={{
          border: '1px solid #6366f1',
          background: '#eef2ff',
          color: '#4338ca',
          borderRadius: 6,
          padding: '3px 10px',
          fontSize: 12,
          fontWeight: 600,
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
        }}>
          <span style={{ fontSize: 11 }}>↗</span>{trimmed}
        </a>
      );
    }

    return <span style={{ color: '#374151' }}>{trimmed}</span>;
  }, [openSupportResource, resourceFileMap]);

  const handleBack = () => navigate(`${returnTo.pathname}${returnTo.search || ''}`, {
    state: { ...(returnTo.state || {}), progressState: returnTo.progressState || null },
  });

  if (isDomainAction) {
    return (
      <div style={{ maxWidth: 980, margin: '0 auto', paddingBottom: 10 }}>
        <PageBackNavigation
          onBack={returnTo?.pathname ? handleBack : () => navigate('/parent/action')}
          label={returnTo?.label || 'Back to Action'}
        />
        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1e3a5f' }}>Guided Action</h1>
        <p style={{ color: '#64748b' }}>{initialSubject} · {initialDomain}</p>
        {progressError ? <div role="alert">{progressError}</div> : null}
        {error ? <div role="alert">{error}</div> : null}
        {familyError ? <div role="alert">{familyError}</div> : null}
        {progressLoading || loading || familyLoading ? (
          <p>Loading resources...</p>
        ) : !progressError && domainResource ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 13, color: '#1f3f77', fontWeight: 700 }}>
                Resource {activeDomainIndex + 1} of {domainActionResources.length}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" disabled={activeDomainIndex === 0}
                  onClick={() => setDomainResourceIndex(activeDomainIndex - 1)}>Previous</button>
                <button type="button" disabled={activeDomainIndex >= domainActionResources.length - 1}
                  onClick={() => setDomainResourceIndex(activeDomainIndex + 1)}>Next</button>
              </div>
            </div>
            <InlineAtHomeSupportPanel
              studentId={selectedStudentId}
              classId={selectedClassId}
              standard={domainResource.code}
              grade={domainResource.grade}
              subject={domainResource.subject}
              strand={domainResource.strand}
              domain={domainResource.domain}
              status="working"
              useFamilySupport={domainResource.hasFamilySupport}
              familySupport={domainResource.familySupport}
              supportEntry={domainResource.supportEntry}
            />
          </>
        ) : !progressError ? (
          <p>No active Learning Zone standards in this domain.</p>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', paddingBottom: 10 }}>
      {returnTo?.pathname && (
        <PageBackNavigation
          onBack={handleBack}
          label={returnTo.label || 'Back'}
        />
      )}
      <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1e3a5f', marginBottom: 6 }}>
        {isFamilyPortal ? 'Guided Action' : 'Action Card'}
      </h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
        Family-friendly resources to support learning in areas that need extra practice.
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18, alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Grade</label>
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 13, minWidth: 110, background: '#fff' }}
          >
            <option value="">All Grades</option>
            {displayGrades.map((g) => (
              <option key={g} value={g}>Grade {g}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Subject</label>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 13, minWidth: 150, background: '#fff' }}
          >
            <option value="">All Subjects</option>
            {displaySubjects.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Search</label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search standards, strands, activities…"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}
      {resourceError && <div style={{ color: '#92400e', marginBottom: 12 }}>{resourceError}</div>}
      {progressError && <div style={{ color: '#92400e', marginBottom: 12 }}>{progressError}</div>}

      {isFamilyMode ? (
        <>
          {familyLoading && !familyDescriptions && (
            <div style={{ color: '#6b7280', padding: 24, textAlign: 'center' }}>Loading…</div>
          )}

          {selectedFamilyEntry ? (
            <div style={{ marginBottom: 18 }}>
              {familyStandardsList.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, color: '#1f3f77', fontWeight: 700 }}>
                    {activeFamilyIndex >= 0
                      ? `Resource ${activeFamilyIndex + 1} of ${familyStandardsList.length}`
                      : `Standard: ${selectedStandard || selectedFamilyEntry.code}`}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeFamilyIndex > 0) {
                          const prev = familyStandardsList[activeFamilyIndex - 1];
                          setSelectedStandard(prev.code);
                          setSelectedSkill(prev.strandName || prev.strand || '');
                          setSelectedDomain(prev.domainName || prev.domain || '');
                        }
                      }}
                      disabled={activeFamilyIndex <= 0}
                      style={{
                        border: '1px solid #9fb7e3',
                        background: activeFamilyIndex <= 0 ? '#e6ebf5' : '#fff',
                        color: activeFamilyIndex <= 0 ? '#94a3b8' : '#1d4ed8',
                        borderRadius: 8,
                        padding: '6px 10px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: activeFamilyIndex <= 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeFamilyIndex >= 0 && activeFamilyIndex < familyStandardsList.length - 1) {
                          const next = familyStandardsList[activeFamilyIndex + 1];
                          setSelectedStandard(next.code);
                          setSelectedSkill(next.strandName || next.strand || '');
                          setSelectedDomain(next.domainName || next.domain || '');
                        }
                      }}
                      disabled={activeFamilyIndex === -1 || activeFamilyIndex >= familyStandardsList.length - 1}
                      style={{
                        border: '1px solid #9fb7e3',
                        background: activeFamilyIndex === -1 || activeFamilyIndex >= familyStandardsList.length - 1 ? '#e6ebf5' : '#fff',
                        color: activeFamilyIndex === -1 || activeFamilyIndex >= familyStandardsList.length - 1 ? '#94a3b8' : '#1d4ed8',
                        borderRadius: 8,
                        padding: '6px 10px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: activeFamilyIndex === -1 || activeFamilyIndex >= familyStandardsList.length - 1 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              <InlineAtHomeSupportPanel
                studentId={selectedStudentId}
                classId={selectedClassId}
                standard={selectedStandard || selectedFamilyEntry?.code}
                grade={selectedGrade || selectedFamilyEntry?.grade}
                subject={selectedSubject || selectedFamilyEntry?.subject}
                strand={selectedSkill || selectedFamilyEntry?.strandName || selectedFamilyEntry?.strand}
                domain={selectedDomain || selectedFamilyEntry?.domainName || selectedFamilyEntry?.domain}
                status={selectedFamilyStatus}
                useFamilySupport={true}
                familySupport={selectedFamilyEntry}
              />
            </div>
          ) : selectedStandard ? (
            <div style={{ marginBottom: 18, padding: 18, border: '1px solid #bfd0e6', borderRadius: 12, background: '#eef3fb' }}>
              <div style={{ color: '#1e3a5f', fontSize: 15, fontWeight: 800, marginBottom: 5 }}>Coming Soon</div>
              <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.5 }}>
                At-home support information for <strong>{selectedStandard}</strong> is not available yet.
              </div>
            </div>
          ) : familyStandardsList.length === 0 && !familyLoading ? (
            <div style={{ color: '#6b7280', fontSize: 14, padding: '24px 0', textAlign: 'center' }}>
              No support resources found for the selected filters.
            </div>
          ) : null}

          {familyStandardsList.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              {Object.entries(familyStrandsGrouped).length > 1 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(familyStrandsGrouped).map(([strandName]) => {
                    const active = String(selectedSkill || '').toLowerCase() === String(strandName || '').toLowerCase();
                    return (
                      <button
                        type="button"
                        key={strandName}
                        onClick={() => {
                          if (active) {
                            setSelectedSkill('');
                          } else {
                            setSelectedSkill(strandName);
                            const first = familyStandardsList.find((e) => (e.strandName || e.strand) === strandName);
                            if (first) {
                              setSelectedStandard(first.code);
                              setSelectedDomain(first.domainName || first.domain || '');
                            }
                          }
                        }}
                        style={{
                          border: `1.5px solid ${active ? '#1d4ed8' : '#cbd5e1'}`,
                          background: active ? '#dbeafe' : '#fff',
                          color: active ? '#1d4ed8' : '#334155',
                          borderRadius: 999,
                          padding: '7px 12px',
                          fontSize: 13,
                          fontWeight: 700,
                          lineHeight: 1.2,
                          cursor: 'pointer',
                        }}
                      >
                        {strandName}
                      </button>
                    );
                  })}
                </div>
              )}

              <div style={{ border: '1px solid #c7d2e8', borderRadius: 12, background: '#f8fbff', padding: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1f3f77', marginBottom: 8 }}>
                  Standards In This View
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto', paddingRight: 2 }}>
                  {familyStandardsList.map((entry, idx) => {
                    const active = normalizeStandardCode(selectedFamilyEntry?.code || selectedStandard) === normalizeStandardCode(entry.code);
                    const rowStatus = progressByStandard.get(normalizeStandardCode(entry.code)) || 'na';
                    const statusUi = progressStatusUi(rowStatus);
                    return (
                      <button
                        type="button"
                        key={`${entry.code}-${idx}`}
                        onClick={() => {
                          setSelectedStandard(entry.code);
                          setSelectedSkill(entry.strandName || entry.strand || '');
                          setSelectedDomain(entry.domainName || entry.domain || '');
                        }}
                        style={{
                          border: `1.5px solid ${active ? '#3b82f6' : '#d6deed'}`,
                          background: active ? '#e8f1ff' : '#fff',
                          color: '#1f2937',
                          borderRadius: 8,
                          padding: '8px 10px',
                          fontSize: 12,
                          lineHeight: 1.35,
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                          <div style={{ fontWeight: 800, color: '#1e3a8a' }}>{entry.code}</div>
                          {selectedStudentId && (
                            <span
                              style={{
                                border: `1px solid ${statusUi.border}`,
                                background: statusUi.bg,
                                color: statusUi.fg,
                                borderRadius: 999,
                                padding: '1px 8px',
                                fontSize: 10,
                                fontWeight: 800,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {statusUi.label}
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#334155' }}>
                          {isFamilyPortal ? (
                            <HighlightVocabText
                              text={entry.friendly || entry.strand || 'Support item'}
                              subject={selectedSubject || entry.subject}
                              vocabBank={vocabBanks[canonicalSubject(selectedSubject || entry.subject)]}
                              active={isFamilyPortal}
                            />
                          ) : (
                            entry.friendly || entry.strand || 'Support item'
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {loading && <div style={{ color: '#6b7280', padding: 24, textAlign: 'center' }}>Loading…</div>}

      {!loading && selectedEntry && (
        <div style={{ border: '1.5px solid #3057a7', borderRadius: 14, background: '#eef3fb', padding: 14, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: '#1f3f77', fontWeight: 700 }}>
              {`Resource ${selectedEntryIndex + 1} of ${focusedEntries.length}`}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={goToPrevious}
                disabled={selectedEntryIndex <= 0}
                style={{
                  border: '1px solid #9fb7e3',
                  background: selectedEntryIndex <= 0 ? '#e6ebf5' : '#fff',
                  color: selectedEntryIndex <= 0 ? '#94a3b8' : '#1d4ed8',
                  borderRadius: 8,
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: selectedEntryIndex <= 0 ? 'not-allowed' : 'pointer',
                }}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={goToNext}
                disabled={selectedEntryIndex === -1 || selectedEntryIndex >= focusedEntries.length - 1}
                style={{
                  border: '1px solid #9fb7e3',
                  background: selectedEntryIndex === -1 || selectedEntryIndex >= focusedEntries.length - 1 ? '#e6ebf5' : '#fff',
                  color: selectedEntryIndex === -1 || selectedEntryIndex >= focusedEntries.length - 1 ? '#94a3b8' : '#1d4ed8',
                  borderRadius: 8,
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: selectedEntryIndex === -1 || selectedEntryIndex >= focusedEntries.length - 1 ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            </div>
          </div>
          <div style={{ background: '#3057a7', color: '#fff', borderRadius: 8, padding: '8px 12px', textAlign: 'center', fontSize: 18, fontWeight: 700, marginBottom: 10 }}>
            {selectedEntryPath || `${selectedSubject || selectedEntry.subject || 'Subject'}${selectedEntry.strand ? ` › ${selectedEntry.strand}` : ''}`}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'stretch', marginBottom: 12 }}>
            <div style={{ background: '#d7e4f8', borderRadius: 12, padding: '20px', color: '#1f2937' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1e3a8a', marginBottom: 10 }}>Learning Outcome</div>
              {formatLearningOutcome(
                selectedEntry,
                selectedSubject || selectedEntry?.subject,
                vocabBanks[canonicalSubject(selectedSubject || selectedEntry?.subject)],
                isFamilyPortal,
              )}
            </div>
            <div style={{ display: 'grid', gap: 10, minWidth: 240 }}>
              {selectedStudentId && (
                <div style={{ background: '#fff', border: '1px solid #c7d2e8', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Student Progress</div>
                  <div
                    style={{
                      border: `1px solid ${progressStatusUi(selectedEntryProgress).border}`,
                      background: progressStatusUi(selectedEntryProgress).bg,
                      color: progressStatusUi(selectedEntryProgress).fg,
                      borderRadius: 999,
                      padding: '8px 12px',
                      fontSize: 12,
                      fontWeight: 800,
                      textAlign: 'center',
                    }}
                  >
                    {progressLoading ? 'Loading...' : progressStatusUi(selectedEntryProgress).label}
                  </div>
                </div>
              )}
              <div style={{ background: '#f3f4f6', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'flex-start', border: '1px solid #d1d5db' }}>
                {selectedEntry.grade && (
                  <div style={{ background: '#e2e8f0', borderRadius: 999, padding: '8px 14px', fontWeight: 800, fontSize: 14, color: '#0f172a', textAlign: 'center' }}>
                    Grade {selectedEntry.grade}
                  </div>
                )}
                {selectedEntry.subject && (
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#334155', textAlign: 'center', border: '1px solid #d1d5db' }}>
                    {selectedEntry.subject}
                  </div>
                )}
                {selectedEntry.domain && (
                  <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', fontWeight: 600 }}>
                    {selectedEntry.domain}
                  </div>
                )}
                {['Phonics', 'Fluency'].includes(selectedEntry.subject) && (
                  <button
                    type="button"
                    onClick={() => openProgressionChart(selectedEntry.subject)}
                    style={{
                      background: '#1d4ed8',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                  >
                    View Progression →
                  </button>
                )}
                {guideSubject === 'phonics' && (
                  <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a', marginBottom: 10 }}>
                      Phonics progression badges
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(68px, 1fr))', gap: 8 }}>
                      {PHONICS_BADGE_LEVELS.map((level) => {
                        const active = phonicsBadgeLevel === level;
                        return (
                          <button
                            key={level}
                            type="button"
                            onClick={() => openProgressionChart('phonics')}
                            style={{
                              border: active ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
                              background: active ? '#1d4ed8' : '#f8fafc',
                              color: active ? '#ffffff' : '#1f2937',
                              borderRadius: 999,
                              padding: '10px 8px',
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 4,
                              minHeight: 72,
                            }}
                          >
                            <div style={{ fontSize: 16, fontWeight: 800 }}>{level}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                              Phonics
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {subjectGuideBadge && (
                  <button
                    type="button"
                    onClick={() => openProgressionChart(guideSubject)}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #1d4ed8',
                      borderRadius: 18,
                      textAlign: 'left',
                      padding: 0,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
                      width: '100%',
                      maxWidth: 280,
                      margin: '0 auto',
                    }}
                  >
                    <div style={{ background: '#1d4ed8', padding: '14px 16px', color: '#fff', fontSize: 13, fontWeight: 800, letterSpacing: 0.3, textAlign: 'center' }}>
                      {subjectGuideBadge.title}
                    </div>
                    {guideGradeLabel && (
                      <div style={{ background: '#eef2ff', padding: '10px 16px', color: '#1d4ed8', fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                        {guideGradeLabel}
                      </div>
                    )}
                    <div style={{ background: guideSubject === 'fluency' ? '#fef3c7' : '#dbeafe', padding: '18px 16px', display: 'grid', placeItems: 'center', minHeight: 120 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', marginBottom: 8, letterSpacing: 1.4 }}>
                        LEXILE LEVEL
                      </div>
                      <div style={{ fontSize: 30, fontWeight: 900, color: '#0f172a', lineHeight: 1.05, textAlign: 'center' }}>
                        {subjectGuideBadge.lexile}
                      </div>
                    </div>
                    <div style={{ background: '#1d4ed8', padding: '12px 16px', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 1.4, textAlign: 'center' }}>
                      WORDS PER MINUTE RANGE
                    </div>
                    <div style={{ background: guideSubject === 'fluency' ? '#fee2e0' : '#cffafe', padding: '18px 16px', textAlign: 'center', minHeight: 100 }}>
                      <div style={{ fontSize: 28, fontWeight: 900, color: '#0f172a', lineHeight: 1.05 }}>{subjectGuideBadge.wpm}</div>
                    </div>
                  </button>
                )}
                <div>Test</div>
              </div>
            </div>
          </div>

          <div style={{ background: '#c4d6f1', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10, minHeight: 90 }}>
            {videoResource.embedUrl ? (
              <div style={{ width: '100%', maxWidth: 980, borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)' }}>
                <iframe
                  title="Action Card video preview"
                  src={videoResource.embedUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: '0' }}
                />
              </div>
            ) : videoResource.url ? (
              <a href={videoResource.url} target="_blank" rel="noreferrer" style={{ fontSize: 14, fontWeight: 700, color: '#1d4ed8', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 22 }}>▶</span>{videoResource.name || 'Open Video Resource'}
              </a>
            ) : videoResource.name ? (
              <div style={{ fontSize: 13, color: '#334155', fontWeight: 700, textAlign: 'center' }}>
                {videoResource.name}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>Coming soon</div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <div style={{ background: '#d2deef', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a8a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 16 }}>💻</span> Digital Resources
              </div>
              {digitalLines.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 0, display: 'flex', flexDirection: 'column', gap: 6, listStyle: 'none' }}>
                  {digitalLines.map((line, i) => (
                    <li key={i}>{renderResourceLine(line)}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>Coming soon</p>
              )}
            </div>

            <div style={{ background: '#d2deef', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a8a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 16 }}>🖨️</span> Print Resources
              </div>
              {printLines.length > 0 ? (
                <ul style={{ margin: 0, listStyle: 'none', paddingLeft: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {printLines.map((line, i) => (
                    <li key={i}>{renderResourceLine(line)}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>Coming soon</p>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && selectedStandard && !selectedEntry && (
        <div style={{ marginBottom: 18, padding: 18, border: '1px solid #bfd0e6', borderRadius: 12, background: '#eef3fb' }}>
          <div style={{ color: '#1e3a5f', fontSize: 15, fontWeight: 800, marginBottom: 5 }}>Coming Soon</div>
          <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.5 }}>
            At-home support information for <strong>{selectedStandard}</strong> is not available yet.
          </div>
        </div>
      )}

      {!loading && Object.entries(grouped).length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(grouped).flatMap(([subject, strands]) =>
              Object.keys(strands).map((strand) => {
                const active = String(selectedSkill || '').toLowerCase() === String(strand).toLowerCase();
                return (
                  <button
                    type="button"
                    key={`${subject}-${strand}`}
                    onClick={() => {
                      setSelectedSubject(subject);
                      setSelectedSkill(strand);
                      const firstForStrand = (strands[strand] || [])[0];
                      setSelectedStandard(firstForStrand?.standard || '');
                      setSelectedDomain('');
                    }}
                    style={{
                      border: `1.5px solid ${active ? '#1d4ed8' : '#cbd5e1'}`,
                      background: active ? '#dbeafe' : '#fff',
                      color: active ? '#1d4ed8' : '#334155',
                      borderRadius: 999,
                      padding: '7px 12px',
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      cursor: 'pointer',
                    }}
                  >
                    {`${subject} • ${strand}`}
                  </button>
                );
              })
            )}
          </div>

          <div style={{ border: '1px solid #c7d2e8', borderRadius: 12, background: '#f8fbff', padding: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1f3f77', marginBottom: 8 }}>
              Standards In This View
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto', paddingRight: 2 }}>
              {focusedEntries.map((entry, idx) => {
                const active = String(selectedEntry?.standard || '').toLowerCase() === String(entry.standard || '').toLowerCase();
                const rowStatus = progressByStandard.get(String(entry.standard || '').toLowerCase()) || 'na';
                const statusUi = progressStatusUi(rowStatus);
                return (
                  <button
                    type="button"
                    key={`${entry.standard}-${idx}`}
                    onClick={() => {
                      setSelectedSubject(entry.subject || '');
                      setSelectedSkill(entry.strand || '');
                      setSelectedStandard(entry.standard || '');
                      setSelectedDomain('');
                    }}
                    style={{
                      border: `1px solid ${active ? '#3b82f6' : '#d6deed'}`,
                      background: active ? '#e8f1ff' : '#fff',
                      color: '#1f2937',
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontSize: 12,
                      lineHeight: 1.35,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                      <div style={{ fontWeight: 800, color: '#1e3a8a' }}>{entry.standard}</div>
                      {selectedStudentId && (
                        <span
                          style={{
                            border: `1px solid ${statusUi.border}`,
                            background: statusUi.bg,
                            color: statusUi.fg,
                            borderRadius: 999,
                            padding: '1px 8px',
                            fontSize: 10,
                            fontWeight: 800,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {statusUi.label}
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#334155' }}>{entry.friendly || entry.strand || 'Support item'}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!loading && Object.keys(grouped).length === 0 && !error && (
        <div style={{ color: '#6b7280', fontSize: 14, padding: '24px 0', textAlign: 'center' }}>
          No support resources found for the selected filters.
        </div>
      )}
        </>
      )}
      {returnTo?.pathname && (
        <PageBackNavigation position="bottom" label={returnTo.label || 'Back'}
          onBack={handleBack} />
      )}
    </div>
  );
}
